import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  ProxyDocumentTypes,
  ChatType,
  ChangeAssessmentStatus,
  queryKeys,
  dateFormats,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Analytics01Icon,
  ArrowLeftRightIcon,
  FileImportIcon,
  PlusSignIcon,
  Delete02Icon,
  MoreHorizontalIcon,
  Refresh01Icon,
} from '@hugeicons/core-free-icons';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { useTranslations } from 'use-intl';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import { MainHeader, MainHeaderBackButton, MainScrollArea, Main } from '@/app/ui/components/main';
import { H1 } from '@/app/ui/components/heading';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/app/ui/components/empty';
import { ChangeIcon } from '@/app/components/shared/EmptyIcons';
import { Button } from '@/app/ui/components/button';
import { createChangeRequestOpenAtom } from '@/app/components/features/supplements/changeAssessments/store';
import { useSetAtom } from 'jotai';
import { useGetChangeAssessmentList } from '@/app/hooks/queries/useChangeAssessments';
import { useMemo, useState } from 'react';
import useRefreshQuery from '@/app/hooks/useRefreshQuery';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/app/ui/components/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/ui/components/table';
import { format } from 'date-fns';
import { CreateChangeRequest } from '@/app/components/features/supplements/changeAssessments/CreateChangeRequest';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { ChangeAssessment } from '@/app/types/models';
import NewAssessmentBadge from '@/app/components/features/supplements/changeAssessments/details/AiAssessment/NewAssessmentBadge';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { DocumentsContextProvider } from '@/app/components/shared/EntityDocuments/DocumentsContext';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/app/ui/components/menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/app/ui/components/alert-dialog';
import {
  useDeleteChangeAssessment,
  useUpdateChangeAssessment,
} from '@/app/hooks/queries/useChangeAssessments';
import { toastManager } from '@/app/ui/components/toast';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import AIChatProvider from '@/app/components/features/aiChat/AIChatContext';
import useEntryChat from '@/app/components/features/aiChat/hooks/useEntryChat';
import { Sidechat } from '@/app/components/shared/Sidechat';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/contract-management/$supplementId/_layout/change-analysis/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractManagement');
  const tFeature = useTranslations('features.changeAnalysis');
  const tGeneral = useTranslations('general');
  const tAssessments = useTranslations('components.changeAssessments');
  const { contractId, projectId, supplementId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const setCreateOpen = useSetAtom(createChangeRequestOpenAtom);
  const queryClient = useQueryClient();
  const [uploadFilesOpen, setUploadFilesOpen] = useState(false);
  const navigate = useNavigate();

  const stream = useEntryChat({
    id: contractId,
    namespace: ChatType.SUPPLEMENT,
    supplementId,
  });

  const { data: assessments, isLoading } = useGetChangeAssessmentList({
    contractSupplementPackageId: supplementId,
  });

  const { data: documents, isSuccess: documentsLoaded } = useEntityDocuments(
    supplementId,
    DocumentTypes.SUPPLEMENT,
  );
  const { areDocumentsIndexed } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.SUPPLEMENT,
    id: supplementId,
    enabled: documentsLoaded && documents.length > 0,
  });

  const isAnyAssessmentInProgress = useMemo(
    () =>
      assessments?.some((assessment) => assessment.status === ChangeAssessmentStatus.IN_PROGRESS) ??
      false,
    [assessments],
  );

  useRefreshQuery({
    isCompleted: !isAnyAssessmentInProgress,
    onRefresh: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.CHANGE_ASSESSMENT_LIST, supplementId],
      });
    },
  });

  const columns: ColumnDef<ChangeAssessment>[] = [
    {
      accessorKey: 'title',
      header: () => tAssessments('table.headers.assessmentName'),
      cell: ({ row }) => <div>{row.original.title}</div>,
    },
    {
      accessorKey: 'documentFilename',
      header: () => tAssessments('table.headers.document'),
      cell: ({ row }) => <div>{row.original.documentFilename}</div>,
    },
    {
      accessorKey: 'verdict',
      header: () => tAssessments('table.headers.status'),
      cell: ({ row }) => <NewAssessmentBadge verdict={row.original.verdict} />,
    },
    {
      accessorKey: 'createdAt',
      header: () => tAssessments('table.headers.assessmentDate'),
      cell: ({ row }) => {
        if (row.original.status === ChangeAssessmentStatus.COMPLETED && row.original.createdAt) {
          return format(row.original.createdAt, dateFormats.dayFirst);
        }
        if (row.original.status === ChangeAssessmentStatus.IN_PROGRESS) {
          return <span className="text-muted">{tAssessments('table.labels.analysing')}</span>;
        }
        if (row.original.status === ChangeAssessmentStatus.FAILED) {
          return <span className="text-destructive">{tAssessments('table.labels.failed')}</span>;
        }
        return null;
      },
    },
    {
      id: 'actions',
      size: 64,
      enableSorting: false,
      cell: ({ row }) => <MenuActions row={row} areDocumentsIndexed={areDocumentsIndexed} />,
      header: '',
    },
  ];

  const table = useReactTable({
    columns,
    data: assessments ?? [],
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <AIChatProvider
      chatType={ChatType.SUPPLEMENT}
      sourceStream={stream}
      chatInitialOpen={false}
      evaluationName={evaluation.name}
      evaluationType={EvaluationTypeLabelsEnum.CONTRACT}
      evaluationId={contractId}
    >
      <Main>
        <Header>
          <HeaderBackButton
            to="/project/$projectId/contracts/$contractId/contract-management/$supplementId"
            params={{ projectId, contractId, supplementId }}
          />
          <HeaderSidebarTrigger />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbMenu>
                <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                  <ProjectTypeIcon type={project.type} />
                  {project.name}
                </BreadcrumbMenuItem>
              </BreadcrumbMenu>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem>
                <BreadcrumbLink
                  to="/project/$projectId/contracts/$contractId"
                  params={{ projectId, contractId }}
                >
                  <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                  <span className="line-clamp-1">{evaluation.name}</span>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="max-md:hidden">
                <BreadcrumbLink
                  to="/project/$projectId/contracts/$contractId/contract-management/$supplementId"
                  params={{ projectId, contractId, supplementId }}
                >
                  <HugeiconsIcon icon={ArrowLeftRightIcon} />
                  {t('titleLong')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="max-md:hidden" />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <HugeiconsIcon icon={Analytics01Icon} />
                  {tFeature('title')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <HeaderSidechatButton />
          <HeaderButton onClick={() => setCreateOpen(true)} aria-label={tFeature('buttons.new')}>
            <HugeiconsIcon icon={PlusSignIcon} />
          </HeaderButton>
        </Header>
        <MainScrollArea>
          <DocumentAlerts
            resourceId={supplementId}
            summaryType={SummarizationNamespaces.SUPPLEMENT}
            onGoToFiles={() =>
              navigate({
                to: '/project/$projectId/contracts/$contractId/contract-management/$supplementId/files',
                params: { projectId, contractId, supplementId },
              })
            }
          />
          <CreateChangeRequest />
          <MainHeader>
            <H1>
              <MainHeaderBackButton
                to="/project/$projectId/contracts/$contractId/contract-management/$supplementId"
                params={{ projectId, contractId, supplementId }}
              />
              {tFeature('title')}
            </H1>
            <Button
              kind="with-icon"
              variant={!assessments || assessments.length === 0 ? 'secondary' : 'primary'}
              onClick={() => setCreateOpen(true)}
            >
              <HugeiconsIcon icon={PlusSignIcon} />
              {tFeature('buttons.new')}
            </Button>
          </MainHeader>
          {isLoading ? (
            <Spinner kind="inset" />
          ) : !assessments || assessments.length === 0 ? (
            <DocumentsContextProvider
              resourceId={supplementId}
              documentsType={DocumentTypes.SUPPLEMENT}
            >
              <Empty>
                <EmptyMedia>
                  <ChangeIcon />
                </EmptyMedia>
                <EmptyContent>
                  <EmptyTitle>{tFeature('empty.title')}</EmptyTitle>
                  <EmptyDescription>{tFeature('empty.description')}</EmptyDescription>
                  <div>
                    <Button kind="with-icon" onClick={() => setCreateOpen(true)}>
                      <HugeiconsIcon icon={PlusSignIcon} />
                      {tFeature('empty.button1')}
                    </Button>
                    <p className="text-muted my-2">{tGeneral('or')}</p>
                    <Button
                      kind="with-icon"
                      variant="secondary"
                      onClick={() => setUploadFilesOpen(true)}
                    >
                      <HugeiconsIcon icon={FileImportIcon} />
                      {tFeature('empty.button2')}
                    </Button>
                  </div>
                </EmptyContent>
              </Empty>
              <FileUploadDialog
                resourceId={supplementId}
                resourceType={DocumentTypes.SUPPLEMENT}
                open={uploadFilesOpen}
                onOpenChange={setUploadFilesOpen}
                detailsQueryKey={[queryKeys.SUPPLEMENT_INDEXED_DOCUMENTS_STATUS, supplementId]}
              />
            </DocumentsContextProvider>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => {
                      if (row.original.status === ChangeAssessmentStatus.COMPLETED) {
                        navigate({
                          to: '/project/$projectId/contracts/$contractId/contract-management/$supplementId/change-analysis/$assessmentId',
                          params: {
                            projectId,
                            contractId,
                            supplementId,
                            assessmentId: row.original.id,
                          },
                        });
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </MainScrollArea>
      </Main>
      <Sidechat />
    </AIChatProvider>
  );
}

function MenuActions({
  row,
  areDocumentsIndexed,
}: {
  row: Row<ChangeAssessment>;
  areDocumentsIndexed: boolean;
}) {
  const tGeneral = useTranslations('general');
  const queryClient = useQueryClient();
  const tAssessments = useTranslations('components.changeAssessments');
  const { supplementId } = Route.useParams();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [rerunAnalysisOpen, setRerunAnalysisOpen] = useState(false);

  const { mutateAsync: deleteAssessment, isPending: isDeleting } =
    useDeleteChangeAssessment(supplementId);
  const { mutateAsync: updateChangeAssessment, isPending: isUpdatingAssessment } =
    useUpdateChangeAssessment(supplementId);

  const handleDelete = async () => {
    await deleteAssessment(row.original.id);
  };

  const handleReRun = async (assessment: ChangeAssessment) => {
    try {
      await updateChangeAssessment({
        name: assessment.title,
        docId: assessment.docId,
      });
      toastManager.add({
        type: 'success',
        title: tAssessments('alerts.reRunSuccess', {
          assessmentName: assessment.title,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.CHANGE_ASSESSMENT_LIST, supplementId],
      });
      setRerunAnalysisOpen(false);
    } catch (error) {
      toastManager.add({
        type: 'error',
        title: tAssessments('alerts.reRunFailed', {
          assessmentName: assessment.title,
        }),
      });
    }
  };

  return (
    <div
      className="text-right"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Menu>
        <MenuTrigger
          render={
            <Button variant="ghost" kind="icon">
              <HugeiconsIcon icon={MoreHorizontalIcon} className="size-5.5 md:size-5" />
            </Button>
          }
        />
        <MenuPopup align="end">
          <MenuItem
            onClick={() => setRerunAnalysisOpen(true)}
            disabled={
              row.original.status === ChangeAssessmentStatus.IN_PROGRESS || !areDocumentsIndexed
            }
          >
            <HugeiconsIcon icon={Refresh01Icon} />
            {tAssessments('buttons.reRunAnalysis')}
          </MenuItem>
          <MenuItem variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <HugeiconsIcon icon={Delete02Icon} />
            {tGeneral('delete')}
          </MenuItem>
        </MenuPopup>
      </Menu>
      <AlertDialog open={rerunAnalysisOpen} onOpenChange={setRerunAnalysisOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>{tGeneral('reRunAnalysis')}?</AlertDialogTitle>
          <AlertDialogDescription>{tGeneral('reRunAnalysisDescription')}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel />
            <Button onClick={() => handleReRun(row.original)}>{tGeneral('reRunAnalysis')}</Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>{tAssessments('alerts.deleteConfirmModal.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {tAssessments('alerts.deleteConfirmModal.subtitle')}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting} />
            <Button variant="destructive" pending={isDeleting} onClick={handleDelete}>
              {tGeneral('delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
